// src/stores/terminationStore.js
import { defineStore } from "pinia";
import { ref } from "vue";
import promotionService from "@/services/hrm/promotion.service";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useDesignationStore } from "@/stores/designationStore";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const usePromotionStore = defineStore("promotion", () => {
  const items = ref([]);
  const pagination = ref({ total: 0, per_page: 25, current_page: 1 });
  const loading = ref(false);
  const error = ref(null);

  const filters = ref({
    search: "",
    department_id: "",
    status: "",
    date_from: "",
    date_to: "",
    per_page: 25,
    page: 1,
  });

  async function fetchAll(extra = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_PROMOTION))) {
      items.value = [];
      pagination.value = { total: 0, per_page: 25, current_page: 1 };
      return;
    }

    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      const res = await promotionService.getAll(params);
      const data = res.data;

      if (data && data.data && data.meta) {
        items.value = data.data;
        pagination.value = {
          total: data.meta.total,
          per_page: data.meta.per_page,
          current_page: data.meta.current_page,
        };
      } else if (data && data.data) {
        // Laravel simple paginate
        items.value = data.data;
        pagination.value = {
          total: data.total ?? data.data.length,
          per_page: data.per_page ?? params.per_page ?? 25,
          current_page: data.current_page ?? 1,
        };
      } else {
        items.value = data;
      }
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch terminations";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  function refreshEmployeeDesignationLists() {
    useEmployeeStore().invalidateEmployeeCaches();
    useDesignationStore().invalidateDesignationCaches();
  }

  async function create(payload) {
    loading.value = true;
    try {
      await promotionService.create(payload);
      refreshEmployeeDesignationLists();
      toast("Promotion created");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to create";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      await promotionService.update(id, payload);
      refreshEmployeeDesignationLists();
      toast("Promotion updated");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    try {
      await promotionService.delete(id);
      toast("Promotion deleted");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    loading.value = true;
    try {
      await promotionService.bulkDelete(ids);
      toast("Selected promotions deleted");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to bulk delete";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items, pagination, loading, error, filters,
    fetchAll, create, update, remove, bulkDelete
  };
});
